import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, FileText } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useSearchArticles } from '@/hooks/useKnowledgeBase';
import { cn } from '@/lib/utils';

interface KBSearchProps {
  onSelect?: (slug: string) => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  playbook: 'Playbook',
  sop: 'SOP',
  guide: 'Guia',
  reference: 'Referência',
};

export function KBSearch({ onSelect }: KBSearchProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const { data: results = [], isLoading } = useSearchArticles(debouncedQuery);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Show results when there's a query
  useEffect(() => {
    setIsOpen(query.length >= 2);
  }, [query]);

  const handleSelect = (slug: string) => {
    setQuery('');
    setIsOpen(false);
    if (onSelect) {
      onSelect(slug);
    } else {
      navigate(`/knowledge-base/${slug}`);
    }
  };

  const highlightMatch = (text: string, searchQuery: string) => {
    if (!searchQuery) return text;
    const regex = new RegExp(`(${searchQuery})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-yellow-200 dark:bg-yellow-800 rounded px-0.5">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar artigos..."
          className="pl-9 pr-9"
        />
        {query && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
            onClick={() => {
              setQuery('');
              setIsOpen(false);
            }}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-lg z-50">
          <ScrollArea className="max-h-80">
            {isLoading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                Buscando...
              </div>
            ) : results.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                Nenhum resultado encontrado
              </div>
            ) : (
              <div className="py-1">
                {results.map((article) => (
                  <button
                    key={article.id}
                    onClick={() => handleSelect(article.slug)}
                    className="w-full text-left px-3 py-2 hover:bg-muted transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <FileText className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate">
                            {highlightMatch(article.title, debouncedQuery)}
                          </span>
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            {CATEGORY_LABELS[article.category] || article.category}
                          </Badge>
                        </div>
                        {article.subcategory && (
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {article.subcategory}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
